import { EllipsisOutlined } from '@ant-design/icons'
import { Button, Popconfirm, Popover, Space, Table } from 'antd'
import { TablePaginationConfig } from 'antd/es/table'
import { ColumnsType } from 'antd/lib/table'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { TABLE_MAX_HEIGHT } from '@/constants/customer'
import routes from '@/constants/routes'
import { Detergent } from '@/types'
import { formatJapaneseNumber, formatPrice } from '@/utils/formatPrice'

import { GhostButton } from '../../style'
import { CustomRowTable } from './styles'

type Props = {
  dataSource: Detergent[] | undefined
  getDetergentsLoading: boolean
  onDelete: (id: Detergent['id']) => void
  page: number
  pageSize: number
  totalItems: number
  onChange: (config: TablePaginationConfig) => void
}

const DetergentsTable = ({
  dataSource,
  getDetergentsLoading,
  onDelete,
  page,
  pageSize,
  totalItems,
  onChange,
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [visibleConfirmDelete, setVisibleConfirmDelete] = useState<Record<string, boolean>>({})

  const columns: ColumnsType<Detergent> = [
    {
      title: t('components.pages.detergents.list.table.name'),
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      render: (record: string) => {
        return <CustomRowTable type="left">{record}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-detergent',
    },
    {
      title: t('components.pages.detergents.list.table.price'),
      dataIndex: 'price',
      key: 'price',
      render: (record: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(formatPrice(record))}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-detergent',
    },
    {
      title: t('components.pages.detergents.list.table.netWeightG'),
      dataIndex: 'netWeightG',
      key: 'netWeightG',
      render: (record: number) => {
        return <CustomRowTable type="right">{record ? formatJapaneseNumber(record) : ''}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-detergent',
    },
    {
      title: t('components.pages.detergents.list.table.netWeightMl'),
      dataIndex: 'netWeightMl',
      key: 'netWeightMl',
      render: (record: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(record) || ''}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-detergent',
    },
    {
      title: t('components.pages.detergents.list.table.specificGravity'),
      dataIndex: 'specificGravity',
      key: 'specificGravity',
      render: (record: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(record) || ''}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-detergent',
    },
    {
      title: t('components.pages.detergents.list.table.costPerMl'),
      dataIndex: 'costPerMl',
      key: 'costPerMl',
      render: (record: number) => {
        return <CustomRowTable type="right">{formatPrice(record)}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-detergent',
    },
    {
      title: t('components.pages.detergents.list.table.remainingQuantity'),
      dataIndex: 'remainingQuantity',
      key: 'remainingQuantity',
      render: (record: number) => {
        return <CustomRowTable type="right">{record ? formatJapaneseNumber(record) : ''}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-detergent',
    },
    {
      title: t('components.pages.detergents.list.table.thresholdQuantity'),
      dataIndex: 'thresholdQuantity',
      key: 'thresholdQuantity',
      render: (record: number) => {
        return <CustomRowTable type="right">{record || ''}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-detergent',
    },
  ]

  columns.push({
    align: 'center' as const,
    render: (text: string, record: Detergent) => {
      return (
        <Space size="middle">
          <Popover
            placement="bottomRight"
            content={
              <Space size="small" direction="vertical">
                <GhostButton size="small" onClick={() => goToDetailDetergentPage(record.id)}>
                  {t('components.pages.detergents.list.table.importGoods')}
                </GhostButton>
                <Button size="small" onClick={() => goToEditDetergentPage(record.id)}>
                  {t('components.pages.admins.list.table.update')}
                </Button>
                <Popconfirm
                  id={record.id.toString()}
                  visible={!!visibleConfirmDelete[record.id]}
                  title={t('components.pages.attendanceLogs.list.table.confirmDelete')}
                  okText={t('common.yes')}
                  cancelText={t('common.no')}
                  onConfirm={() => {
                    onDelete(record.id)
                  }}
                  onCancel={() => setVisibleConfirmDelete({})}
                >
                  <Button
                    size="small"
                    danger
                    onClick={() => setVisibleConfirmDelete({ [record.id]: true })}
                    loading={false}
                  >
                    {t('components.pages.admins.list.table.delete')}
                  </Button>
                </Popconfirm>
              </Space>
            }
          >
            <Button>
              <EllipsisOutlined />
            </Button>
          </Popover>
        </Space>
      )
    },
  })

  const goToEditDetergentPage = (id: Detergent['id']) => {
    const path = routes.updateDetergent.path.replace(':id', id.toString())
    navigate(path)
  }

  const goToDetailDetergentPage = (id: Detergent['id']) => {
    const path = routes.detergentHistories.path.replace(':id', id.toString())
    navigate(path)
  }

  return (
    <>
      <Table
        rowKey="id"
        bordered
        dataSource={dataSource}
        columns={columns}
        loading={getDetergentsLoading}
        pagination={{
          pageSize,
          current: page,
          total: totalItems,
          showSizeChanger: false,
        }}
        onChange={onChange}
        scroll={{ x: 'max-content', y: TABLE_MAX_HEIGHT }}
      />
    </>
  )
}

DetergentsTable.displayName = 'DetergentsTable'
export default DetergentsTable
